import * as Yup from 'yup';

const FIRST_ERROR = 0;

export function getErrorsFromValidationError(
  validationError: Yup.ValidationError
): Record<string, unknown> {
  const innerErrors =
    validationError.inner?.length > 0
      ? validationError.inner
      : validationError.path
        ? [validationError]
        : [];

  return innerErrors.reduce((errors: Record<string, unknown>, error) => {
    if (!error.path) {
      return errors;
    }

    return {
      ...errors,
      [error.path]: error.errors[FIRST_ERROR]
    };
  }, {});
}

export function getNestedArrayErrorsFromValidationError(
  validationError: Yup.ValidationError
): Record<string, unknown> {
  return validationError.inner.reduce((errors: Record<string, unknown>, error) => {
    const path = error.path;
    if (!path) {
      return errors;
    }

    if (path.indexOf('[') > 0) {
      const objectName = path.substring(0, path.indexOf('['));
      const index = path.substring(path.indexOf('[') + 1, path.indexOf(']'));
      const subObject = path.substring(path.indexOf('.') + 1);
      const originalObject = Array.isArray(errors[objectName])
        ? [...(errors[objectName] as unknown[])]
        : [];

      originalObject[index as unknown as number] =
        originalObject[index as unknown as number] !== undefined
          ? {
              ...(originalObject[index as unknown as number] as object),
              [subObject]: error.errors[FIRST_ERROR]
            }
          : { [subObject]: error.errors[FIRST_ERROR] };

      return { ...errors, [objectName]: originalObject };
    }

    return { ...errors, [path]: error.errors[FIRST_ERROR] };
  }, {});
}

type ErrorMapper = (validationError: Yup.ValidationError) => Record<string, unknown>;

function createFormikValidateWithErrorMapper<T extends unknown[]>(
  getValidationSchema: (values: unknown, ...args: T) => Yup.AnySchema,
  mapErrors: ErrorMapper,
  ...extraArgs: T
) {
  return (values: unknown) => {
    const schema = getValidationSchema(values, ...extraArgs);

    try {
      schema.validateSync(values, { abortEarly: false });
      return {};
    } catch (error) {
      if (Yup.ValidationError.isError(error)) {
        return mapErrors(error);
      }

      throw error;
    }
  };
}

export function createFormikValidate<T extends unknown[]>(
  getValidationSchema: (values: unknown, ...args: T) => Yup.AnySchema,
  ...extraArgs: T
) {
  return createFormikValidateWithErrorMapper(
    getValidationSchema,
    getErrorsFromValidationError,
    ...extraArgs
  );
}

export function createFormikValidateWithNestedArrayErrors<T extends unknown[]>(
  getValidationSchema: (values: unknown, ...args: T) => Yup.AnySchema,
  ...extraArgs: T
) {
  return createFormikValidateWithErrorMapper(
    getValidationSchema,
    getNestedArrayErrorsFromValidationError,
    ...extraArgs
  );
}

export function createFormikValidateFromSchema(schema: Yup.AnySchema) {
  return (values: unknown) => {
    try {
      schema.validateSync(values, { abortEarly: false });
      return {};
    } catch (error) {
      if (Yup.ValidationError.isError(error)) {
        return getErrorsFromValidationError(error);
      }

      throw error;
    }
  };
}

let yupExtensionsRegistered = false;

export function registerYupStringExtensions() {
  if (yupExtensionsRegistered) {
    return;
  }

  yupExtensionsRegistered = true;

  Yup.addMethod(Yup.string, 'zeroAndEmptyCheck', function (this: Yup.StringSchema, errorMessage: string) {
    return this.test('test-number-validation', errorMessage, function (value) {
      const { path, createError } = this;
      return (value && value !== '0') || createError({ path, message: errorMessage });
    });
  });
}

declare module 'yup' {
  interface StringSchema {
    zeroAndEmptyCheck(errorMessage: string): this;
  }
}
